import React from 'react';
import { useNavigate } from 'react-router-dom';
import { User, X, LogOut, Settings, PlayCircle, CreditCard, Users } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import { performLogout } from '../utils/logout';

const isClientRole = (role: string | null | undefined) =>
  role === 'user' || role === 'cliente';

interface UserProfileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Solo para rol cliente: abre el modal del tutorial. */
  onOpenTutorial?: () => void;
}

export const UserProfileDrawer: React.FC<UserProfileDrawerProps> = ({ isOpen, onClose, onOpenTutorial }) => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const profile = useSelector((state: RootState) => state.auth.profile);
  const [userRole, setUserRole] = React.useState<string | null>(null);
  const dispatch = useDispatch();

  React.useEffect(() => {
    const getUserRole = async () => {
      if (user && profile) {
        setUserRole(profile.role || null);
      }
    };
    getUserRole();
  }, [user, profile]);

  const handleSignOut = async () => {
    onClose();
    await performLogout(dispatch, navigate);
  };

  const handleNavigation = (path: string) => {
    onClose();
    navigate(path);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    // Solo cerrar si el clic fue directamente en el overlay
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // No mostrar el drawer si no hay usuario autenticado
  if (!user || !profile) {
    console.log('🚫 Drawer no mostrado - No hay usuario o perfil:', { user: !!user, profile: !!profile });
    return null;
  }

  console.log('✅ Renderizando UserProfileDrawer:', { user: user?.email, profile: profile?.role });

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-60 z-50"
      onClick={handleOverlayClick}
    >
      <div className="fixed right-0 top-0 h-full w-80 bg-[#181C23] shadow-2xl flex flex-col">
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Perfil de Usuario</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Cerrar drawer"
          >
            <X size={22} />
          </button>
        </div>

        <div className="p-4 flex items-center space-x-3 border-b border-gray-700">
          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          <div className="flex flex-col">
            <span className="text-white font-medium text-sm leading-tight">{user?.email}</span>
            <span className="text-gray-400 text-xs leading-tight">{userRole || 'Usuario'}</span>
          </div>
        </div>

        <div className="flex-1 p-4 space-y-2 overflow-y-auto">
          <button
            onClick={() => handleNavigation('/profile')}
            className="w-full flex items-center space-x-3 px-3 py-2 text-gray-200 hover:bg-gray-700 rounded-lg transition-colors text-sm"
          >
            <User size={18} />
            <span>Editar Perfil</span>
          </button>

          {onOpenTutorial && isClientRole(userRole) && (
            <button
              onClick={() => {
                onOpenTutorial();
                onClose();
              }}
              className="w-full flex items-center space-x-3 px-3 py-2 text-gray-200 hover:bg-gray-700 rounded-lg transition-colors text-sm"
            >
              <PlayCircle size={18} />
              <span>Ver tutorial</span>
            </button>
          )}

          {isClientRole(userRole) && (
            <button
              onClick={() => handleNavigation('/potential-clients')}
              className="w-full flex items-center space-x-3 px-3 py-2 text-gray-200 hover:bg-gray-700 rounded-lg transition-colors text-sm"
            >
              <Users size={18} />
              <span>Posibles Clientes</span>
            </button>
          )}

          {userRole === 'super_admin' && (
            <>
              <button
                onClick={() => handleNavigation('/admin/users')}
                className="w-full flex items-center space-x-3 px-3 py-2 text-gray-200 hover:bg-gray-700 rounded-lg transition-colors text-sm"
              >
                <Settings size={18} />
                <span>Administrar Usuarios</span>
              </button>
              <button
                onClick={() => handleNavigation('/admin/parameters')}
                className="w-full flex items-center space-x-3 px-3 py-2 text-gray-200 hover:bg-gray-700 rounded-lg transition-colors text-sm"
              >
                <Settings size={18} />
                <span>Parámetros del Sistema</span>
              </button>
              <button
                onClick={() => handleNavigation('/admin/credit-consumption')}
                className="w-full flex items-center space-x-3 px-3 py-2 text-gray-200 hover:bg-gray-700 rounded-lg transition-colors text-sm"
              >
                <CreditCard size={18} />
                <span>Consumo de Créditos</span>
              </button>
            </>
          )}
        </div>

        <div className="p-4 border-t border-gray-700">
          <button
            onClick={handleSignOut}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm font-semibold shadow"
          >
            <LogOut size={18} />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </div>
    </div>
  );
}; 